import { mount } from 'svelte'
import App from './App.svelte'
import './style.css'
import { startMcoAuth, acceptHostBearer } from './lib/mcoAuth.svelte'

/* Iframe-bearer hash bootstrap: when DC embeds us, it may seed the URL
 * with `#mco=<token>&exp=<iso>` so we can pick up the bearer SYNCHRONOUSLY
 * before any API call fires. Defends against the first-time-on-new-device
 * race where mu's own localStorage is empty AND postMessage timing is
 * unreliable (especially on mobile). We strip the hash from window.location
 * immediately after reading so the bearer doesn't leak into browser
 * history / bookmark UX. URL fragments never reach servers (not sent in
 * Referer or fetch), so this is the safer transport vs. query params. */
/** Returns the bearer from the URL hash if present, else null. Also
 *  strips the hash from window.location to keep it out of bookmarks. */
function readHashBearer(): { token: string; expiresAt: string | null } | null {
  if (typeof window === 'undefined') return null
  const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : ''
  if (!raw) return null
  const params = new URLSearchParams(raw)
  const token = params.get('mco')
  if (!token) return null
  try {
    history.replaceState(null, '', window.location.pathname + window.location.search)
  } catch { /* no-op in restricted contexts */ }
  return { token, expiresAt: params.get('exp') }
}

const hashBearer = readHashBearer()

// MCO auth — read cached bearer + register the cross-origin postMessage
// listener before the app mounts. The blocking overlay (McoAuthGate) is
// mounted in App.svelte and only shows when MU is standalone (not iframed
// inside DC, which provides its own gate).
startMcoAuth()

// Hash bearer wins over a stale localStorage entry — DC just handed us
// the bearer it currently holds, which is the freshest known good. This
// runs AFTER startMcoAuth so it overrides any cached-but-stale value.
if (hashBearer) {
  acceptHostBearer({ token: hashBearer.token, expiresAt: hashBearer.expiresAt, identity: null })
}

const app = mount(App, {
  target: document.getElementById('app')!,
})

export default app
